const FIREBASE_PREFIX = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
const PREVIEW_CLOUDFRONT_URL = process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL;

// DesignReviewPostProcess.process_type values (backend). Refinements build on an existing
// prototype and can stack; we don't build prompts for them yet, so the dropdown is hidden.
const REFINEMENT_PREVIEW_PROCESS_TYPE = 2;

// Build a Firebase storage URL for a raw frame path. Mirrors the pattern used in
// CategoryDetailView.jsx (rawImageUrls) and PageVariantPanel.jsx (ensureDesignImageUrl).
const buildFirebaseUrl = framePath => (
  framePath ? `${FIREBASE_PREFIX}${encodeURIComponent(framePath)}?alt=media` : null
);

// Public URL of our generated prototype's HTML. Same construction the page uses for the
// iframe (index.jsx). Optional — a prototype may not have an HTML upload.
const resolveHtmlUrl = postProcess => {
  const htmlKey = postProcess?.result_data?.html_upload_info?.key;

  return htmlKey ? `${PREVIEW_CLOUDFRONT_URL}${htmlKey}` : null;
};

// Resolve the original (critiqued) design image for this prototype. The backend already
// returns the fully-formed URL in result_data.base_image_url; fall back to building it
// from the review's frame_images when a page number is available.
const resolveImageUrl = (postProcess, designReview, page) => {
  const baseImageUrl = postProcess?.result_data?.base_image_url;

  if (baseImageUrl) {
    return baseImageUrl;
  }

  const frameImages = designReview?.extra_data?.frame_images;

  if (Array.isArray(frameImages) && Number.isInteger(page) && page >= 1) {
    return buildFirebaseUrl(frameImages[page - 1]);
  }

  return null;
};

// Feedback relevant to a UX Prototype (page-level) variant. Mirrors the filter in
// CategoryDetailView.jsx pageEligibleFeedback.
const getPageFeedback = (designReview, page) => (
  (designReview?.final_feedback || []).filter(item => (
    item?.page === page
    && item?.dismiss !== true
    && !item?.isDeeperInsight
    && item?.category !== 'deeper_insight'
    && item?.category !== 'positive'
  ))
);

// The single deeper-insight this New Idea variant was generated from. Matched by uuid,
// mirroring DesignInspirationPanel.jsx.
const getInsightFeedback = (designReview, insightUuid) => {
  const insights = designReview?.extra_data?.post_processes?.deeper_insights?.feedback || [];

  return insights.find(item => String(item?.uuid) === String(insightUuid)) || null;
};

const cleanText = value => (typeof value === 'string' ? value.trim() : '');

// Turn one page-level feedback item into a readable numbered block.
const formatPageFeedback = (item, index) => {
  const highlight = cleanText(item?.feedback_highlight) || cleanText(item?.label);
  const details = Array.isArray(item?.detailed_feedback)
    ? item.detailed_feedback.map(cleanText).filter(Boolean)
      .join(' ')
    : cleanText(item?.detailed_feedback);
  const reason = cleanText(item?.reason);

  const lines = [`${index + 1}. ${highlight}`];

  if (details) {
    lines.push(`   What to do: ${details}`);
  }

  if (reason) {
    lines.push(`   Why: ${reason}`);
  }

  return lines.join('\n');
};

// Turn the matched deeper-insight into a readable block.
const formatInsightFeedback = insight => {
  const headline = cleanText(insight?.feedback_highlight)
    || cleanText(insight?.high_impact_recommendation);
  const details = cleanText(insight?.detailed_feedback);
  const reason = cleanText(insight?.reason);
  const steps = Array.isArray(insight?.action_steps)
    ? insight.action_steps.map(cleanText).filter(Boolean)
    : [];

  const lines = [headline];

  if (details) {
    lines.push('', `Details: ${details}`);
  }

  if (reason) {
    lines.push('', `Why: ${reason}`);
  }

  if (steps.length) {
    lines.push('', 'Action steps:', ...steps.map(step => `- ${step}`));
  }

  return lines.join('\n');
};

/**
 * Build a tool-agnostic prompt that a user can paste into Claude Code, v0.app, Lovable, etc.
 * to recreate this specific prototype. Contains the original design image URL, the feedback
 * relevant to this prototype, and (when available) a link to our generated prototype's HTML,
 * which serves as the reference implementation of what the recreation should achieve.
 *
 * @returns {string|null} the prompt, or null when it cannot be built (no payload / no feedback).
 */
export const buildPreviewPrompt = ({ post_process: postProcess, design_review: designReview }) => {
  // Refinements aren't supported yet — hide the dropdown for them.
  if (postProcess?.process_type === REFINEMENT_PREVIEW_PROCESS_TYPE) {
    return null;
  }

  const payload = postProcess?.input_payload;

  if (!payload) {
    return null;
  }

  const isInsight = payload.insight_uuid != null;
  const page = isInsight ? null : Number(payload.page);

  const imageUrl = resolveImageUrl(postProcess, designReview, page);
  const htmlUrl = resolveHtmlUrl(postProcess);

  let feedbackBlock = '';

  if (isInsight) {
    const insight = getInsightFeedback(designReview, payload.insight_uuid);

    if (!insight) {
      return null;
    }

    feedbackBlock = formatInsightFeedback(insight);
  }
  else {
    const items = getPageFeedback(designReview, page);

    if (!items.length) {
      return null;
    }

    feedbackBlock = items.map(formatPageFeedback).join('\n\n');
  }

  if (!imageUrl || !cleanText(feedbackBlock)) {
    return null;
  }

  const lines = [
    'I want you to recreate a UI screen in clean, production-quality code.',
    '',
    '1. Original design — the screen we started from:',
    imageUrl,
    '',
    '2. UX improvements to apply to that design:',
    '',
    feedbackBlock,
  ];

  if (htmlUrl) {
    lines.push(
      '',
      '3. Reference prototype — a working implementation that already applies the above',
      'improvements to the original design. Treat it as the source of truth for the intended',
      'goal, layout, and behaviour:',
      htmlUrl,
      '',
      'Rebuild this from scratch as a single, self-contained, responsive implementation. Match'
        + ' everything the reference prototype achieves — do not drop or compromise any of its'
        + ' behaviour, and do not add features beyond it. Do not copy the reference HTML'
        + ' verbatim; produce your own clean, well-structured version of the same result.'
    );
  }
  else {
    lines.push(
      '',
      'Build this as a single, self-contained, responsive implementation of the original design'
        + ' with the improvements above applied.'
    );
  }

  return lines.join('\n');
};

export default buildPreviewPrompt;
